import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CheckCircle, Package, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useCart } from "@/hooks/use-cart";
import { usePromo } from "@/hooks/use-promo";

export const Route = createFileRoute("/confirmation")({
  head: () => ({
    meta: [
      { title: "Commande confirmée — angle-shop.com" },
      { name: "description", content: "Votre commande a été confirmée. Merci pour votre achat sur angle-shop.com." },
    ],
  }),
  component: ConfirmationPage,
});

function ConfirmationPage() {
  const { clearCart } = useCart();
  const { consumeCode } = usePromo();

  useEffect(() => {
    consumeCode();
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
        >
          <div className="w-20 h-20 rounded-full bg-badge-success/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-badge-success" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
            Order Confirmed!
          </h1>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed max-w-md mx-auto">
            Thank you for your purchase. You will receive a confirmation email with tracking details.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 bg-card rounded-2xl p-8 shadow-card"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Package className="w-5 h-5 text-electric" />
            <span className="font-semibold text-foreground">Delivery Details</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Shipped within 24h on business days. Estimated delivery in 2-4 days.
          </p>
          <div className="mt-4 p-3 rounded-xl bg-electric/5 text-sm text-electric font-medium">
            ⚡ Test mode — no real payment was made
          </div>
        </motion.div>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-electric text-electric-foreground font-semibold shadow-electric hover:opacity-90 transition-opacity"
          >
            Back to home <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/catalog"
            className="inline-flex items-center px-6 py-3 rounded-xl border border-border text-foreground font-semibold hover:bg-accent transition-colors"
          >
            Continue shopping
          </Link>
        </div>
      </div>
      <Footer />
    </main>
  );
}
